/**
 * Google Analytics connect API for Revenue Attribution.
 *
 * GraphQL against the revenue intelligence service (ra-data). The consent URL is
 * built in the browser (see googleOAuthUrl.ts) — client_id is public, so only the
 * code->token exchange needs a server, and that is what lives here.
 */
import { gql } from "@apollo/client";

import { revenueClient } from "store/graphql/apollo/clients";
import { getEnvironmentId } from "utils/localStorage";

import { AI_SOURCE_REGEX } from "./copy";

export type Ga4Result<T> = { ok: true; data: T } | { ok: false; message: string };

function gqlMessage(error: unknown, fallback: string): string {
    const e = error as { graphQLErrors?: { message?: string }[]; message?: string };
    return e?.graphQLErrors?.[0]?.message || e?.message || fallback;
}

// ── Exchange ─────────────────────────────────────────────────────────────────

export const EXCHANGE_GA_CODE = gql`
    mutation ExchangeGoogleAnalyticsCode($input: ExchangeGoogleAnalyticsCodeInput!) {
        exchangeGoogleAnalyticsCode(input: $input) {
            refreshToken
            accessToken
            expiresIn
            grantedScopes
            hasAnalyticsScope
        }
    }
`;

export interface ExchangeGaCodeResult {
    refreshToken: string;
    accessToken: string;
    expiresIn: number;
    /** Can be narrower than requested — Google lets users untick individual scopes. */
    grantedScopes: string[];
    /** False means analytics.readonly was declined; every probe would return PERMISSION_DENIED. */
    hasAnalyticsScope: boolean;
}

/**
 * Trades the authorization code for tokens.
 *
 * `redirectUri` must be byte-identical to the one used to build the consent URL,
 * or Google rejects the exchange with redirect_uri_mismatch.
 */
export async function exchangeGa4Code(code: string, redirectUri: string): Promise<Ga4Result<ExchangeGaCodeResult>> {
    try {
        const res = await revenueClient.mutate<{ exchangeGoogleAnalyticsCode: ExchangeGaCodeResult }>({
            mutation: EXCHANGE_GA_CODE,
            variables: { input: { code, redirectUri } },
            fetchPolicy: "no-cache"
        });
        const data = res.data?.exchangeGoogleAnalyticsCode;
        if (!data) return { ok: false, message: "The connection did not complete. Please try again." };
        return { ok: true, data };
    } catch (e) {
        return { ok: false, message: gqlMessage(e, "Could not complete the Google Analytics connection.") };
    }
}

// ── Setup details (probes) ───────────────────────────────────────────────────

/**
 * Called twice by design: without `propertyId` it returns only `accounts`, for the
 * picker; with one, every probe runs and populates the channel, key-event and
 * revenue steps in a single round trip.
 *
 * Field names verified against ra-data-engine-api/graph/schema.graphqls.
 */
export const GA_SETUP_DETAILS = gql`
    query GoogleAnalyticsSetupDetails($input: GoogleAnalyticsSetupDetailsInput!) {
        googleAnalyticsSetupDetails(input: $input) {
            canProceed
            remainingQuota
            accounts {
                accountId
                accountName
                properties {
                    propertyId
                    displayName
                    propertyType
                }
            }
            propertyDetails {
                propertyId
                displayName
                timeZone
                currencyCode
                createTime
                industry
                serviceLevel
                accountId
                propertyType
            }
            channelConfig {
                status
                nativeChannelPresent
                nativeChannelCoverage
                totalAiSessions
                problems
                aiSources {
                    source
                    sessions
                    landedIn
                }
            }
            keyEvents {
                hasAnyKeyEvents
                totalKeyEvents30d
                keyEvents {
                    name
                    count
                    keyEventCount
                }
                otherEvents {
                    name
                    count
                    keyEventCount
                }
                suggestedKeyEvents
            }
            revenue {
                propertyId
                currency
                period {
                    start
                    end
                }
                hasKeyEvents
                hasGa4Revenue
                conversionEvents {
                    name
                    eventCount
                    keyEvents
                    totalRevenue
                    eventValue
                    revenueSource
                    needsStatedValue
                }
                otherEvents {
                    name
                    count
                    keyEventCount
                }
                totalRevenue90d
                totalEventValue90d
                purchaseRevenue90d
            }
            volume {
                sessions30d
                keyEvents30d
                sufficientForPageLevel
                minKeyEventsForPageLevel
            }
            pageCardinality {
                uniquePages
                otherBucketPresent
                atRisk
                rowLimit
                reportedRowCount
            }
            aiSourceMix {
                totalAiSessions30d
                totalSessions30d
                aiShareOfTotal
                totalAiKeyEvents30d
                byVendor {
                    vendor
                    sessions
                    keyEvents
                    rawSources
                }
            }
            errors {
                probe
                code
                message
                actionRequired
                tenantActionable
                blocking
            }
            warnings {
                probe
                code
                message
                actionRequired
                tenantActionable
                blocking
            }
        }
    }
`;

export interface Ga4PropertySummary {
    /** Bare numeric id — the "properties/" prefix is stripped server-side. */
    propertyId: string;
    displayName: string;
    propertyType: string;
}

export interface Ga4AccountSummary {
    accountId: string;
    accountName: string;
    properties: Ga4PropertySummary[];
}

export interface Ga4PropertyDetails {
    propertyId: string;
    displayName: string;
    /** Day boundaries follow this, not the viewer's timezone. */
    timeZone: string;
    currencyCode: string;
    createTime: string;
    industry: string;
    serviceLevel: string;
    accountId: string;
    propertyType: string;
}

export type Ga4ChannelConfigStatus = "OK" | "BROKEN" | "MISSING" | "NO_AI_TRAFFIC";

export interface Ga4ChannelConfigProbe {
    status: Ga4ChannelConfigStatus;
    nativeChannelPresent: boolean;
    /** Share of our AI sessions the native channel captured, 0-1. */
    nativeChannelCoverage: number;
    totalAiSessions: number;
    problems: string[];
    aiSources: { source: string; sessions: number; landedIn: string }[];
}

export interface Ga4EventCount {
    name: string;
    count: number;
    keyEventCount: number;
}

export interface Ga4KeyEventsProbe {
    /** False is expected, not an error — it drives the "mark a key event" screen. */
    hasAnyKeyEvents: boolean;
    keyEvents: Ga4EventCount[];
    otherEvents: Ga4EventCount[];
    suggestedKeyEvents: string[];
    totalKeyEvents30d: number;
}

/**
 * Where a conversion event's worth came from, and so which input the wizard renders.
 * - GA4_NATIVE: GA4 reported real revenue; nothing to ask the tenant.
 * - EVENT_VALUE: the event carries a "value" parameter; pre-fill it rather than ask.
 * - NONE: GA4 knows of no worth; the tenant must state a value.
 */
export type Ga4RevenueSource = "GA4_NATIVE" | "EVENT_VALUE" | "NONE";

/** The window a probe read, as absolute dates in the property's own time zone. */
export interface Ga4ReportPeriod {
    start: string;
    end: string;
}

/** One key event and everything GA4 knows about what it is worth. */
export interface Ga4ConversionEvent {
    name: string;
    /** Every occurrence, versus the subset GA4 counted as a key event. */
    eventCount: number;
    keyEvents: number;
    /** Only ecommerce events (e.g. GA4's `purchase`) populate this; custom events do not. */
    totalRevenue: number;
    /** Sum of the event's "value" parameter — the only worth a custom conversion can carry. */
    eventValue: number;
    revenueSource: Ga4RevenueSource;
    /** True only when revenueSource is NONE — branch on this to render a deal-value input. */
    needsStatedValue: boolean;
}

export interface Ga4RevenueProbe {
    propertyId: string;
    /** The property's configured currency. Empty only when the property reports none. */
    currency: string;
    period: Ga4ReportPeriod;
    /** False is the most common state for a new tenant: no key events are marked at all. */
    hasKeyEvents: boolean;
    /** Whether GA4 carries native revenue anywhere in the window. */
    hasGa4Revenue: boolean;
    /** Every event with keyEvents > 0, ordered by key event count descending. */
    conversionEvents: Ga4ConversionEvent[];
    /** Everything else — candidates to suggest when the tenant marked the wrong event or none. */
    otherEvents: Ga4EventCount[];
    totalRevenue90d: number;
    totalEventValue90d: number;
    purchaseRevenue90d: number;
}

export interface Ga4VolumeProbe {
    sessions30d: number;
    keyEvents30d: number;
    sufficientForPageLevel: boolean;
    minKeyEventsForPageLevel: number;
}

export interface Ga4PageCardinalityProbe {
    uniquePages: number;
    otherBucketPresent: boolean;
    atRisk: boolean;
    rowLimit: number;
    reportedRowCount: number;
}

export interface Ga4AiSourceMixProbe {
    totalAiSessions30d: number;
    totalSessions30d: number;
    aiShareOfTotal: number;
    totalAiKeyEvents30d: number;
    byVendor: { vendor: string; sessions: number; keyEvents: number; rawSources: string[] }[];
}

export interface Ga4ProbeFailure {
    probe: string;
    code: string;
    message: string;
    actionRequired: string;
    tenantActionable: boolean;
    blocking: boolean;
}

export interface Ga4SetupDetails {
    canProceed: boolean;
    remainingQuota: number;
    accounts: Ga4AccountSummary[];
    /** null means the probe didn't run or failed — check errors/warnings. */
    propertyDetails: Ga4PropertyDetails | null;
    channelConfig: Ga4ChannelConfigProbe | null;
    keyEvents: Ga4KeyEventsProbe | null;
    revenue: Ga4RevenueProbe | null;
    volume: Ga4VolumeProbe | null;
    pageCardinality: Ga4PageCardinalityProbe | null;
    aiSourceMix: Ga4AiSourceMixProbe | null;
    errors: Ga4ProbeFailure[];
    warnings: Ga4ProbeFailure[];
}

export async function fetchGa4SetupDetails(opts: {
    refreshToken: string;
    propertyId?: string;
    forceRefresh?: boolean;
}): Promise<Ga4Result<Ga4SetupDetails>> {
    const environmentId = currentEnvironmentId();
    if (!environmentId) {
        return { ok: false, message: "No environment selected — can't read your Analytics setup." };
    }
    try {
        const res = await revenueClient.query<{ googleAnalyticsSetupDetails: Ga4SetupDetails }>({
            query: GA_SETUP_DETAILS,
            variables: {
                input: {
                    environmentId,
                    refreshToken: opts.refreshToken,
                    ...(opts.propertyId ? { propertyId: opts.propertyId } : {}),
                    ...(opts.forceRefresh ? { forceRefresh: true } : {})
                }
            },
            fetchPolicy: "no-cache"
        });
        const data = res.data?.googleAnalyticsSetupDetails;
        if (!data) return { ok: false, message: "No setup details returned." };
        return { ok: true, data };
    } catch (e) {
        return { ok: false, message: gqlMessage(e, "Could not read your Google Analytics setup.") };
    }
}

// ── Channel groups (AI Assistant channel diagnosis) ──────────────────────────
//
// A separate, on-demand read (googleAnalyticsChannelGroups) used by the channel
// step to check whether the property has an "AI Assistant" channel whose regex
// covers every source we credit to AI. It reads the Admin API and costs no Data
// API quota, so it is not folded into the setup probe fan-out.

export const GA_CHANNEL_GROUPS = gql`
    query GoogleAnalyticsChannelGroups($input: GoogleAnalyticsChannelGroupsInput!) {
        googleAnalyticsChannelGroups(input: $input) {
            channelGroups {
                channelGroupId
                propertyId
                displayName
                systemDefined
                primary
                rules {
                    displayName
                    conditionsTruncated
                    conditions {
                        fieldName
                        matchType
                        values
                        negated
                        isRegex
                    }
                }
            }
            error {
                probe
                code
                message
                actionRequired
                tenantActionable
                blocking
            }
        }
    }
`;

export type Ga4ChannelGroupMatchType =
    | "EXACT"
    | "BEGINS_WITH"
    | "ENDS_WITH"
    | "CONTAINS"
    | "FULL_REGEXP"
    | "PARTIAL_REGEXP"
    | "IN_LIST"
    | "UNSPECIFIED";

export interface Ga4ChannelGroupCondition {
    fieldName: string;
    matchType: Ga4ChannelGroupMatchType;
    values: string[];
    negated: boolean;
    isRegex: boolean;
}

export interface Ga4ChannelGroupRule {
    displayName: string;
    conditions: Ga4ChannelGroupCondition[];
    conditionsTruncated: boolean;
}

export interface Ga4ChannelGroup {
    channelGroupId: string;
    propertyId: string;
    displayName: string;
    systemDefined: boolean;
    primary: boolean;
    rules: Ga4ChannelGroupRule[];
}

export async function fetchGa4ChannelGroups(opts: {
    refreshToken: string;
    propertyId: string;
    forceRefresh?: boolean;
}): Promise<Ga4Result<Ga4ChannelGroup[]>> {
    const environmentId = currentEnvironmentId();
    if (!environmentId) {
        return { ok: false, message: "No environment selected — can't read your channel groups." };
    }
    try {
        const res = await revenueClient.query<{
            googleAnalyticsChannelGroups: { channelGroups: Ga4ChannelGroup[]; error: Ga4ProbeFailure | null };
        }>({
            query: GA_CHANNEL_GROUPS,
            variables: {
                input: {
                    environmentId,
                    refreshToken: opts.refreshToken,
                    propertyId: opts.propertyId,
                    ...(opts.forceRefresh ? { forceRefresh: true } : {})
                }
            },
            fetchPolicy: "no-cache"
        });
        const data = res.data?.googleAnalyticsChannelGroups;
        if (!data) return { ok: false, message: "No channel groups returned." };
        if (data.error) return { ok: false, message: data.error.message || "Could not read your channel groups." };
        return { ok: true, data: data.channelGroups ?? [] };
    } catch (e) {
        return { ok: false, message: gqlMessage(e, "Could not read your Google Analytics channel groups.") };
    }
}

export type AiChannelOutcome = "OK" | "BROKEN" | "MISSING";

export interface AiChannelEvaluation {
    /** OK = every required source is present; BROKEN = an AI channel exists but is
     *  missing some; MISSING = no AI channel found at all. */
    outcome: AiChannelOutcome;
    /** Required AI sources not covered by the matched channel (order-independent). */
    missing: string[];
    /** Display name of the rule we matched as the AI channel, if any. */
    matchedChannel: string | null;
}

/** Strip regex escaping and normalise so `chatgpt\.com` and `chatgpt.com` compare equal. */
function normaliseSource(token: string): string {
    return token.replace(/\\/g, "").trim().toLowerCase();
}

/**
 * Decide whether the property's channel groups include an AI channel that covers
 * every source we credit to AI. Matching is by content, not channel name: for each
 * rule we gather the positive (non-negated) source tokens from its conditions —
 * splitting regex alternations and value lists alike — and pick the rule that
 * covers the most required sources. Order does not matter; extra sources are fine.
 */
export function evaluateAiChannel(groups: Ga4ChannelGroup[]): AiChannelEvaluation {
    const required = Array.from(new Set(AI_SOURCE_REGEX.split("|").map(normaliseSource).filter(Boolean)));
    const requiredSet = new Set(required);

    let best: { name: string; present: Set<string> } | null = null;
    const coverage = (present: Set<string>) => required.filter((r) => present.has(r)).length;

    for (const group of groups) {
        for (const rule of group.rules) {
            const present = new Set<string>();
            for (const cond of rule.conditions) {
                if (cond.negated) continue; // a NOT excludes, so it can't "cover" a source
                for (const value of cond.values) {
                    for (const token of value.split("|")) {
                        const norm = normaliseSource(token);
                        if (norm) present.add(norm);
                    }
                }
            }
            const hits = coverage(present);
            if (hits > 0 && (!best || hits > coverage(best.present))) {
                best = { name: rule.displayName, present };
            }
        }
    }

    if (!best) return { outcome: "MISSING", missing: required, matchedChannel: null };
    const matched = best;
    const missing = required.filter((r) => !matched.present.has(r));
    return { outcome: missing.length === 0 ? "OK" : "BROKEN", missing, matchedChannel: matched.name };
}

// ── Flow B: Airbyte-managed OAuth ────────────────────────────────────────────
//
// Distinct from Flow A above. Flow A's refreshToken drives the wizard probes;
// Flow B's secretId provisions the Airbyte source. They are NOT interchangeable,
// and both are needed — hence two consent screens.
//
// Airbyte runs this OAuth itself and holds the tokens, so our client_id and the
// token can no longer drift apart, which used to break provisioning with an
// unreadable connector error.

export const GA_OAUTH_INIT = gql`
    mutation GoogleAnalyticsOAuthInit($input: GoogleAnalyticsOAuthInitInput!) {
        googleAnalyticsOAuthInit(input: $input) {
            consentUrl
        }
    }
`;

/**
 * Where Airbyte returns the user once it has handled Google's callback.
 *
 * Not registered with Google — Google only ever sees Airbyte's own redirect URI —
 * so we can point it at the wizard page and keep the persisted setup state rather
 * than bouncing through a dedicated route.
 */
export function airbyteRedirectUri(): string {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}${window.location.pathname}`;
}

export async function initGa4AirbyteOAuth(redirectUri: string): Promise<Ga4Result<{ consentUrl: string }>> {
    const environmentId = currentEnvironmentId();
    if (!environmentId) return { ok: false, message: "No environment selected — can't start the sync." };
    try {
        const res = await revenueClient.mutate<{ googleAnalyticsOAuthInit: { consentUrl: string } }>({
            mutation: GA_OAUTH_INIT,
            variables: { input: { environmentId, redirectUri } },
            fetchPolicy: "no-cache"
        });
        const data = res.data?.googleAnalyticsOAuthInit;
        if (!data?.consentUrl) return { ok: false, message: "No consent URL returned." };
        return { ok: true, data };
    } catch (e) {
        return { ok: false, message: gqlMessage(e, "Could not start the Google authorisation for syncing.") };
    }
}

/**
 * Reads secret_id off the callback.
 *
 * URLSearchParams, never a split on "secret_id=" — splitting captures the
 * trailing &state=… into the value, which Airbyte embeds in the secret
 * coordinate and then rejects with "That secret was not found in the store!".
 */
export function parseAirbyteSecretId(search: string): string | null {
    return new URLSearchParams(search).get("secret_id");
}

/**
 * The secret is obtained on step 1 but spent on the final step, so it has to
 * survive the steps in between.
 *
 * Known trade-off, taken deliberately: the handle points into Airbyte's secret
 * store and is documented as short-lived, so a user who lingers in the wizard
 * may find it gone by the time they provision. That surfaces as the
 * "secret was not found in the store" error on Create connection, and the fix
 * is to re-authorise on step 1 rather than to retry.
 */
const AIRBYTE_SECRET_KEY = "citedby.revenueAttribution.airbyteSecretId";
const AIRBYTE_SECRET_AT_KEY = "citedby.revenueAttribution.airbyteSecretAt";

/**
 * How long we treat an Airbyte secret handle as usable.
 *
 * ASSUMPTION, not a documented value: neither ra-data-engine-api nor the Airbyte
 * client pins a TTL for secret_id — the lifetime belongs to Airbyte's secret
 * store. Ten minutes is deliberately conservative, so we prompt a re-authorise
 * before Airbyte returns "That secret was not found in the store!". Set this to
 * the real figure once Airbyte's is known; it is the only number to change.
 */
export const AIRBYTE_SECRET_TTL_MS = 10 * 60 * 1000;

export function storeAirbyteSecretId(secretId: string): void {
    try {
        sessionStorage.setItem(AIRBYTE_SECRET_KEY, secretId);
        sessionStorage.setItem(AIRBYTE_SECRET_AT_KEY, String(Date.now()));
    } catch {
        /* storage blocked — provisioning will ask the user to re-authorise */
    }
}

export function readStoredAirbyteSecretId(): string | null {
    try {
        return sessionStorage.getItem(AIRBYTE_SECRET_KEY);
    } catch {
        return null;
    }
}

/** Epoch ms when the current secret stops being trusted, or null if none. */
export function airbyteSecretExpiresAt(): number | null {
    try {
        const at = sessionStorage.getItem(AIRBYTE_SECRET_AT_KEY);
        if (!at) return null;
        const obtained = Number(at);
        if (!Number.isFinite(obtained)) return null;
        return obtained + AIRBYTE_SECRET_TTL_MS;
    } catch {
        return null;
    }
}

/** True when a secret exists but is past its window — block and re-authorise. */
export function isAirbyteSecretExpired(now = Date.now()): boolean {
    if (!readStoredAirbyteSecretId()) return false;
    const expiresAt = airbyteSecretExpiresAt();
    // No timestamp means we can't vouch for it; treat as expired rather than
    // letting provisioning fail with an opaque store error.
    if (expiresAt === null) return true;
    return now >= expiresAt;
}

export function clearAirbyteSecretId(): void {
    try {
        sessionStorage.removeItem(AIRBYTE_SECRET_KEY);
        sessionStorage.removeItem(AIRBYTE_SECRET_AT_KEY);
    } catch {
        /* no-op */
    }
}

// ── Start ingestion ──────────────────────────────────────────────────────────

/**
 * Creates the Airbyte source + connection and kicks off the first sync — the same
 * mutation QuickBooks/Stripe/Chargebee use, with the GA4 config variant.
 *
 * Hand-written rather than the generated hook because the local
 * schema/revenue-report.graphql predates googleAnalyticsConfig. Once that schema
 * is synced and `npm run generate-graphql` is re-run, this can move to
 * useCreateAirbyteIntegrationMutation like the other integrations.
 */
export const CREATE_GA4_INTEGRATION = gql`
    mutation CreateAirbyteIntegration($input: CreateAirbyteIntegrationInput!) {
        createAirbyteIntegration(input: $input) {
            sourceId
            connectionId
            syncStartedAt
            syncStatus
            jobId
            connectionExist
        }
    }
`;

export interface Ga4IntegrationResult {
    sourceId: string;
    connectionId: string;
    syncStartedAt: string;
    syncStatus: string;
    jobId: number;
    /** True when a connection already existed — a re-run, not a first sync. */
    connectionExist: boolean;
}

export async function startGa4Sync(opts: {
    propertyIds: string[];
    /** From the Airbyte callback. Supersedes refreshToken/accessToken entirely. */
    secretId: string;
    /** How far back to backfill. Defaults to now — we only accrue from today. */
    syncStartTime?: Date;
}): Promise<Ga4Result<Ga4IntegrationResult>> {
    const environmentId = currentEnvironmentId();
    if (!environmentId) return { ok: false, message: "No environment selected — can't start the sync." };
    if (opts.propertyIds.length === 0) return { ok: false, message: "Choose a property before starting the sync." };

    // Matches the format the other integrations send: second precision, Z suffix.
    const syncStartTime = `${(opts.syncStartTime ?? new Date()).toISOString().split(".")[0]}Z`;

    try {
        const res = await revenueClient.mutate<{ createAirbyteIntegration: Ga4IntegrationResult }>({
            mutation: CREATE_GA4_INTEGRATION,
            variables: {
                input: {
                    environmentId,
                    integrationType: "GOOGLE_ANALYTICS",
                    syncStartTime,
                    googleAnalyticsConfig: {
                        propertyIds: opts.propertyIds,
                        authMode: "OAUTH",
                        // No refreshToken/accessToken — secretId supersedes them.
                        secretId: opts.secretId
                    }
                }
            },
            fetchPolicy: "no-cache"
        });
        const data = res.data?.createAirbyteIntegration;
        if (!data) return { ok: false, message: "The sync did not start. Please try again." };
        return { ok: true, data };
    } catch (e) {
        return { ok: false, message: gqlMessage(e, "Could not start the Analytics sync.") };
    }
}

// ── Refresh token handling ───────────────────────────────────────────────────
//
// The exchange hands the refresh token back to the browser and every probe takes
// it as an argument, so it has to live somewhere between the redirect landing and
// the property picker. sessionStorage rather than localStorage: it dies with the
// tab instead of persisting to disk. Worth revisiting — a long-lived Google
// credential in browser storage is a bigger blast radius than a session cookie.

const REFRESH_TOKEN_KEY = "citedby.revenueAttribution.refreshToken";

export function storeRefreshToken(token: string): void {
    try {
        sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
    } catch {
        /* storage blocked — the caller still holds it in memory for this flow */
    }
}

export function readRefreshToken(): string | null {
    try {
        return sessionStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
        return null;
    }
}

export function clearRefreshToken(): void {
    try {
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
        /* no-op */
    }
}

/** Probes are scoped to an environment; same source QuickBooks/Stripe use. */
export function currentEnvironmentId(): string {
    return getEnvironmentId() ?? "";
}
